/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { formatDateTime } from '../../i18n';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetDebugTemplatesQuery,
  type ScenarioTemplate as SeedTemplate,
} from '../../api/seasonsAdminApi';
import { useListScenariosQuery } from '../../api/savesV2Api';
import type { ScenarioTemplate as DbTemplate } from '../../types/saves';

const { Text, Paragraph } = Typography;

const KIND_COLORS: Record<string, string> = {
  tutorial: 'geekblue',
  mechanical: 'cyan',
  explanatory: 'purple',
  competitive: 'volcano',
  campaign: 'magenta',
};

const emptyCell = <Text type="secondary">—</Text>;

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  return formatDateTime(value) || '—';
};

const kindTag = (kind?: string | null) => (kind
  ? <Tag color={KIND_COLORS[kind] || 'default'}>{kind}</Tag>
  : emptyCell);

const asNumber = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const asText = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

const countOf = (value: unknown): number | null => (Array.isArray(value) ? value.length : null);

/** Нормализованный срез одной стороны — семена и строка БД зовут поля по-разному. */
interface SideFacts {
  title: string | null;
  kind: string | null;
  version: number | null;
  durationDays: number | null;
  startDate: string | null;
  beds: number | null;
  plantings: number | null;
  goals: number | null;
  /// «Есть ли приз» — сам список наград не показываем, важен факт: до колонки
  /// `rewards` в scenario_templates приз объявлялся в сиде и терялся на границе
  /// БД, и заметить это было нечем.
  hasRewards: boolean | null;
}

interface FactRow {
  key: string;
  label: string;
  value: string | number | null;
}

interface FieldDiff {
  key: string;
  label: string;
  seed: string;
  db: string;
}

type ReconStatus = 'seed_only' | 'db_only' | 'diverged' | 'synced';

interface ReconRow {
  slug: string;
  title: string;
  kind: string | null;
  seed: SeedTemplate | null;
  db: DbTemplate | null;
  seedFacts: SideFacts | null;
  dbFacts: SideFacts | null;
  status: ReconStatus;
  diffs: FieldDiff[];
}

const readSeed = (seed: SeedTemplate): SideFacts => ({
  title: asText(seed.title),
  kind: asText(seed.kind),
  version: asNumber(seed.version),
  durationDays: asNumber(seed.durationDays),
  startDate: asText(seed.worldSpec?.startDate),
  beds: countOf(seed.worldSpec?.beds),
  plantings: countOf(seed.worldSpec?.plantings),
  goals: countOf(seed.goals),
  hasRewards: Boolean(seed.rewards),
});

const readDb = (db: DbTemplate): SideFacts => {
  const world = db.worldSpec;
  return {
    title: asText(db.title),
    kind: asText(db.kind),
    version: asNumber(db.version),
    durationDays: asNumber(db.durationDays),
    startDate: asText(world?.startDate) || asText(world?.start_date),
    beds: countOf(world?.beds),
    plantings: countOf(world?.plantings),
    goals: countOf(db.goals),
    hasRewards: Boolean(db.rewards),
  };
};

/**
 * Поля, по которым решаем «каталог отстал». Зеркалят `comparable()` из
 * scripts/seedScenarioCatalog.js — это СОДЕРЖИМОЕ шаблона.
 *
 * ВЕРСИИ ЗДЕСЬ НЕТ, и это не упущение. Номера версий у двух сторон живут в
 * разных пространствах: в сиде это авторская версия шаблона (всегда 1), а в
 * каталоге — монотонный счётчик публикаций слага (ScenarioTemplatePublisher
 * берёт getNextVersionForSlug). Сравнение их между собой объявляло расхождение
 * у КАЖДОГО опубликованного сценария и — что хуже — лечилось «переиздать»
 * ровно в обратную сторону: каждая публикация увеличивала номер в БД и
 * отодвигала его от сидовой единицы ещё дальше.
 */
const COMPARED_FIELDS: Array<{ key: keyof SideFacts; labelKey: string }> = [
  { key: 'title', labelKey: 'catalog.fields.title' },
  { key: 'kind', labelKey: 'catalog.fields.kind' },
  { key: 'durationDays', labelKey: 'catalog.fields.durationDays' },
  { key: 'startDate', labelKey: 'catalog.fields.startDate' },
  { key: 'beds', labelKey: 'catalog.fields.beds' },
  { key: 'plantings', labelKey: 'catalog.fields.plantings' },
  { key: 'goals', labelKey: 'catalog.fields.goals' },
  { key: 'hasRewards', labelKey: 'catalog.fields.hasRewards' },
];

const factText = (value: string | number | boolean, t: TFunction): string => {
  if (typeof value === 'boolean') return value ? t('common:actions.yes') : t('common:actions.no');
  return String(value);
};

/**
 * Отсутствие поля на одной из сторон — не расхождение: список каталога может не
 * отдавать worldSpec/durationDays вовсе. Сравниваем только то, что дали обе.
 */
const buildDiffs = (seed: SideFacts, db: SideFacts, t: TFunction): FieldDiff[] => {
  const diffs: FieldDiff[] = [];
  COMPARED_FIELDS.forEach(({ key, labelKey }) => {
    const left = seed[key];
    const right = db[key];
    if (left === null || right === null) return;
    if (String(left) === String(right)) return;
    diffs.push({ key: String(key), label: t(labelKey), seed: factText(left, t), db: factText(right, t) });
  });
  return diffs;
};

const statusTag = (status: ReconStatus, t: TFunction) => {
  if (status === 'seed_only') {
    return (
      <Tooltip title={t('catalog.status.seedOnlyTooltip')}>
        <Tag color="orange">{t('catalog.status.seedOnly')}</Tag>
      </Tooltip>
    );
  }
  if (status === 'db_only') {
    return (
      <Tooltip title={t('catalog.status.dbOnlyTooltip')}>
        <Tag color="purple">{t('catalog.status.dbOnly')}</Tag>
      </Tooltip>
    );
  }
  if (status === 'diverged') {
    return (
      <Tooltip title={t('catalog.status.divergedTooltip')}>
        <Tag color="red">{t('catalog.status.diverged')}</Tag>
      </Tooltip>
    );
  }
  return <Tag color="green">{t('catalog.status.synced')}</Tag>;
};

const factRows = (facts: SideFacts, t: TFunction, extra: FactRow[] = []): FactRow[] => ([
  { key: 'title', label: t('catalog.fields.title'), value: facts.title },
  { key: 'kind', label: t('catalog.fields.kind'), value: facts.kind },
  { key: 'version', label: t('catalog.fields.version'), value: facts.version },
  { key: 'durationDays', label: t('catalog.fields.durationDays'), value: facts.durationDays },
  { key: 'startDate', label: t('catalog.fields.startDate'), value: facts.startDate },
  ...extra,
  { key: 'beds', label: t('catalog.fields.beds'), value: facts.beds },
  { key: 'plantings', label: t('catalog.fields.plantings'), value: facts.plantings },
  { key: 'goals', label: t('catalog.fields.goals'), value: facts.goals },
]);

const FactsPanel: React.FC<{
  title: string;
  rows: FactRow[] | null;
  diffKeys: Set<string>;
  emptyText: string;
}> = ({ title, rows, diffKeys, emptyText }) => (
  <Card size="small" title={title}>
    {rows ? (
      <Descriptions size="small" column={1} bordered>
        {rows.map((row) => {
          const highlighted = diffKeys.has(row.key);
          const value = row.value === null || row.value === ''
            ? emptyCell
            : <Text type={highlighted ? 'danger' : undefined} strong={highlighted}>{row.value}</Text>;
          return (
            <Descriptions.Item key={row.key} label={row.label}>{value}</Descriptions.Item>
          );
        })}
      </Descriptions>
    ) : (
      <Text type="secondary">{emptyText}</Text>
    )}
  </Card>
);

const ScenarioCatalogPage: React.FC = () => {
  const { t } = useTranslation('scenarios_admin');
  const navigate = useNavigate();
  const {
    data: seedsData,
    isFetching: seedsLoading,
    error: seedsError,
    refetch: refetchSeeds,
  } = useGetDebugTemplatesQuery();
  const {
    data: catalogData,
    isFetching: catalogLoading,
    error: catalogError,
    refetch: refetchCatalog,
  } = useListScenariosQuery({ limit: 200 });

  const seeds = useMemo<SeedTemplate[]>(() => seedsData?.templates || [], [seedsData]);
  const catalog = useMemo<DbTemplate[]>(() => catalogData || [], [catalogData]);

  const rows = useMemo<ReconRow[]>(() => {
    const dbBySlug = new Map<string, DbTemplate>();
    catalog.forEach((item) => {
      const previous = dbBySlug.get(item.slug);
      // Каталог может отдать несколько версий одного slug — держим старшую.
      if (!previous || (asNumber(item.version) ?? 0) > (asNumber(previous.version) ?? 0)) {
        dbBySlug.set(item.slug, item);
      }
    });

    const result: ReconRow[] = seeds.map((seed) => {
      const db = dbBySlug.get(seed.slug) || null;
      const seedFacts = readSeed(seed);
      const dbFacts = db ? readDb(db) : null;
      const diffs = dbFacts ? buildDiffs(seedFacts, dbFacts, t) : [];
      const status: ReconStatus = !db
        ? 'seed_only'
        : diffs.length > 0 ? 'diverged' : 'synced';
      return {
        slug: seed.slug,
        title: seedFacts.title || seed.slug,
        kind: seedFacts.kind,
        seed,
        db,
        seedFacts,
        dbFacts,
        status,
        diffs,
      };
    });

    const seedSlugs = new Set(seeds.map((seed) => seed.slug));
    dbBySlug.forEach((db) => {
      if (seedSlugs.has(db.slug)) return;
      const dbFacts = readDb(db);
      result.push({
        slug: db.slug,
        title: dbFacts.title || db.slug,
        kind: dbFacts.kind,
        seed: null,
        db,
        seedFacts: null,
        dbFacts,
        status: 'db_only',
        diffs: [],
      });
    });

    return result;
  }, [seeds, catalog, t]);

  const outOfSync = useMemo(
    () => rows.filter((row) => row.status !== 'synced').length,
    [rows],
  );

  const errorDetail = (err: unknown): string | null => {
    if (!err) return null;
    const detail = (err as { data?: { message?: string } })?.data?.message;
    return detail || t('catalog.requestFailed');
  };

  const columns: ColumnsType<ReconRow> = [
    {
      title: t('catalog.columns.scenario'),
      key: 'slug',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{row.slug}</Text>
        </Space>
      ),
    },
    {
      title: t('catalog.fields.kind'),
      key: 'kind',
      width: 130,
      render: (_, row) => kindTag(row.kind),
    },
    {
      title: t('catalog.columns.inSeeds'),
      key: 'seed',
      width: 190,
      render: (_, row) => {
        if (!row.seedFacts) return <Tag>{t('catalog.columns.absent')}</Tag>;
        const duration = row.seedFacts.durationDays;
        return (
          <Space size={4}>
            <Tag icon={<CheckCircleOutlined />} color="green">
              v{row.seedFacts.version ?? '—'}
            </Tag>
            <Text type="secondary">{duration === null ? '—' : t('catalog.durationShort', { days: duration })}</Text>
          </Space>
        );
      },
    },
    {
      title: t('catalog.columns.inCatalog'),
      key: 'db',
      width: 250,
      render: (_, row) => {
        if (!row.db || !row.dbFacts) {
          return <Tag icon={<CloseCircleOutlined />} color="red">{t('catalog.columns.notPublished')}</Tag>;
        }
        return (
          <Space direction="vertical" size={0}>
            <Tag icon={<CheckCircleOutlined />} color="green" style={{ marginInlineEnd: 0 }}>
              v{row.dbFacts.version ?? '—'}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDate(row.db.publishedAt)}
            </Text>
          </Space>
        );
      },
    },
    {
      title: t('common:labels.status'),
      key: 'status',
      width: 170,
      render: (_, row) => statusTag(row.status, t),
    },
    {
      title: t('common:labels.actions'),
      key: 'actions',
      width: 170,
      render: (_, row) => {
        if (!row.seed) {
          return (
            <Tooltip title={t('catalog.actions.nothingToPublish')}>
              <Button size="small" disabled icon={<CloudUploadOutlined />}>{t('catalog.actions.publish')}</Button>
            </Tooltip>
          );
        }
        return (
          <Button
            size="small"
            type={row.status === 'synced' ? 'default' : 'primary'}
            icon={<CloudUploadOutlined />}
            onClick={() => navigate(`/admin/scenarios/author?seed=${encodeURIComponent(row.slug)}`)}
          >
            {row.status === 'synced' ? t('catalog.actions.republish') : t('catalog.actions.publish')}
          </Button>
        );
      },
    },
  ];

  return (
    <MainLayout>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={t('catalog.title')}
            loading={seedsLoading && catalogLoading && rows.length === 0}
            extra={(
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { refetchSeeds(); refetchCatalog(); }}
              >
                {t('common:actions.refresh')}
              </Button>
            )}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('catalog.sourceOfTruth')}
            />
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              <Trans
                t={t}
                i18nKey="catalog.syncNote"
                components={{ code: <Text code /> }}
              />
            </Paragraph>

            {seedsError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('catalog.errors.seedsFailed', { detail: errorDetail(seedsError) })}
              />
            )}
            {catalogError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('catalog.errors.catalogFailed', { detail: errorDetail(catalogError) })}
              />
            )}

            <Row gutter={16}>
              <Col span={8}>
                <Statistic title={t('catalog.stats.seeds')} value={seeds.length} />
              </Col>
              <Col span={8}>
                <Statistic title={t('catalog.columns.inCatalog')} value={catalog.length} />
              </Col>
              <Col span={8}>
                <Statistic
                  title={t('catalog.stats.outOfSync')}
                  value={outOfSync}
                  valueStyle={outOfSync > 0 ? { color: '#cf1322' } : undefined}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card size="small" title={t('catalog.reconTitle')}>
            <Table<ReconRow>
              rowKey={(row) => row.slug}
              columns={columns}
              dataSource={rows}
              loading={seedsLoading || catalogLoading}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              locale={{ emptyText: t('catalog.empty') }}
              expandable={{
                expandedRowRender: (row) => {
                  const diffKeys = new Set(row.diffs.map((diff) => diff.key));
                  return (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <FactsPanel
                            title={t('catalog.panels.seedTitle')}
                            rows={row.seedFacts
                              ? factRows(row.seedFacts, t, [
                                { key: 'terminalDate', label: t('catalog.fields.terminalDate'), value: asText(row.seed?.terminalDate) },
                              ])
                              : null}
                            diffKeys={diffKeys}
                            emptyText={t('catalog.panels.seedEmpty')}
                          />
                        </Col>
                        <Col span={12}>
                          <FactsPanel
                            title={t('catalog.panels.dbTitle')}
                            rows={row.dbFacts
                              ? factRows(row.dbFacts, t, [
                                { key: 'publishedAt', label: t('catalog.fields.publishedAt'), value: formatDate(row.db?.publishedAt) },
                                { key: 'visibility', label: t('catalog.fields.visibility'), value: asText(row.db?.visibility) },
                              ])
                              : null}
                            diffKeys={diffKeys}
                            emptyText={t('catalog.panels.dbEmpty')}
                          />
                        </Col>
                      </Row>

                      {row.seedFacts && row.dbFacts && (
                        row.diffs.length > 0 ? (
                          <Card size="small" title={t('catalog.diffs.title', { n: row.diffs.length })}>
                            <Table<FieldDiff>
                              rowKey={(diff) => diff.key}
                              size="small"
                              pagination={false}
                              dataSource={row.diffs}
                              columns={[
                                { title: t('catalog.diffs.field'), dataIndex: 'label', width: 220 },
                                {
                                  title: t('catalog.diffs.seed'),
                                  dataIndex: 'seed',
                                  render: (value: string) => <Text type="danger">{value}</Text>,
                                },
                                {
                                  title: t('catalog.diffs.db'),
                                  dataIndex: 'db',
                                  render: (value: string) => <Text type="danger">{value}</Text>,
                                },
                              ]}
                            />
                          </Card>
                        ) : (
                          <Text type="secondary">
                            {t('catalog.diffs.none')}
                          </Text>
                        )
                      )}
                    </Space>
                  );
                },
              }}
            />
          </Card>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default ScenarioCatalogPage;
